import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dashboardService from '../../servicios/dashboardService';
import api from '../../servicios/api';
import '../../estilos/Dashboard.css';

function Dashboard({ getTopCriticalTasks, getTopCriticalMaterials }) {
  const [curvaS, setCurvaS] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [vistaPrecios] = useState('todos'); // usado por calcularDatosPrecios() para el reporte PDF/correo
  const [datosPrecios, setDatosPrecios] = useState({ subcontratos: [], materiales: [] });

  // Estados para envío de reporte por correo
  const [mostrarFormReporte, setMostrarFormReporte] = useState(false);
  const [emailReporte, setEmailReporte] = useState('');
  const [enviandoReporte, setEnviandoReporte] = useState(false);
  const [mensajeReporte, setMensajeReporte] = useState({ texto: '', tipo: '' });

  // Cargar datos
  useEffect(() => {
    const cargarDatos = async () => {
      setCargando(true);
      setError(null);
      try {
        const curva = await dashboardService.obtenerCurvaS(filtroTipo);
        setCurvaS(curva);
        
        // Cargar datos de precios (incluyendo completados)
        const [subcontratosRes, materialesRes, subcontratosCompletadosRes, materialesCompletadosRes] = await Promise.all([
          api.get('/subcontratos'),
          api.get('/materiales'),
          api.get('/subcontratos-completados'),
          api.get('/materiales-completados')
        ]);

        const subcontratosResponse = subcontratosRes.data;
        const materialesResponse = materialesRes.data;
        const subcontratosCompletadosResponse = subcontratosCompletadosRes.data;
        const materialesCompletadosResponse = materialesCompletadosRes.data;
        
        // La API devuelve { cantidad, subcontratos } o { materiales } o directamente arrays
        const subcontratosArray = Array.isArray(subcontratosResponse) ? subcontratosResponse : (subcontratosResponse.subcontratos || []);
        const materialesArray = Array.isArray(materialesResponse) ? materialesResponse : (materialesResponse.materiales || []);
        const subcontratosCompletadosArray = Array.isArray(subcontratosCompletadosResponse) ? subcontratosCompletadosResponse : (subcontratosCompletadosResponse.subcontratos || []);
        const materialesCompletadosArray = Array.isArray(materialesCompletadosResponse) ? materialesCompletadosResponse : (materialesCompletadosResponse.materiales || []);
        
        // Combinar subcontratos y materiales activos con completados
        const todosLosSubcontratos = [...subcontratosArray, ...subcontratosCompletadosArray];
        const todosLosMateriales = [...materialesArray, ...materialesCompletadosArray];
        
        setDatosPrecios({ subcontratos: todosLosSubcontratos, materiales: todosLosMateriales });
      } catch (error) {
        console.error('Error al cargar datos del dashboard:', error);
        setError('Error al cargar los datos');
      } finally {
        setCargando(false);
      }
    };

    cargarDatos();
  }, [filtroTipo]);


  if (cargando) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
      </div>
    );
  }

  if (!curvaS) {
    return (
      <div className="dashboard-error">
        <p>No hay datos disponibles para el dashboard</p>
        <p className="dashboard-hint">Agrega subcontratos o materiales a tu proyecto para ver el progreso</p>
      </div>
    );
  }

  // Subcontratos y materiales vencidos o próximos a vencer (3 días o menos)
  const subcontratosEnRiesgo = typeof getTopCriticalTasks === 'function'
    ? getTopCriticalTasks().filter((t) => t.daysUntilDue <= 3)
    : [];
  const materialesEnRiesgo = typeof getTopCriticalMaterials === 'function'
    ? getTopCriticalMaterials().filter((m) => m.daysUntilDue <= 3)
    : [];

  // Calcular datos de comparación de precios
  const calcularDatosPrecios = () => {
    const items = [];
    
    // Procesar subcontratos (subcontratos)
    if ((vistaPrecios === 'todos' || vistaPrecios === 'subcontratos') && Array.isArray(datosPrecios.subcontratos)) {
      datosPrecios.subcontratos.forEach((subcontrato) => {
        if (subcontrato.procesosCompletados) {
          // Obtener objeto de procesos
          const procesos = subcontrato.procesosCompletados instanceof Map 
            ? Object.fromEntries(subcontrato.procesosCompletados)
            : subcontrato.procesosCompletados;
          
          // Acceso directo solo a los procesos relevantes
          const procesosRelevantes = [
            { nombre: 'contratoFirmado', etiqueta: 'Contrato Firmado' },
            { nombre: 'ocAprobada', etiqueta: 'OC Aprobada' }
          ];
          
          procesosRelevantes.forEach(({ nombre, etiqueta }) => {
            const proceso = procesos[nombre];
            if (proceso?.precioPresupuestado && proceso?.precioCierre) {
              items.push({
                tipo: 'Subcontrato',
                nombre: subcontrato.texto || subcontrato.text,
                especialidad: subcontrato.especialidad || subcontrato.specialty,
                etapa: etiqueta,
                presupuestado: proceso.precioPresupuestado,
                cierre: proceso.precioCierre,
                diferencia: proceso.precioPresupuestado - proceso.precioCierre,
                porcentaje: ((proceso.precioPresupuestado - proceso.precioCierre) / proceso.precioPresupuestado * 100).toFixed(2)
              });
            }
          });
        }
      });
    }
    
    // Procesar materiales
    if ((vistaPrecios === 'todos' || vistaPrecios === 'materiales') && Array.isArray(datosPrecios.materiales)) {
      datosPrecios.materiales.forEach(material => {
        if (material.procesosCompletados) {
          // Obtener objeto de procesos
          const procesos = material.procesosCompletados instanceof Map 
            ? Object.fromEntries(material.procesosCompletados)
            : material.procesosCompletados;
          
          // Acceso directo solo al proceso relevante
          const proceso = procesos['ocAprobada'];
          if (proceso?.precioPresupuestado && proceso?.precioCierre) {
            items.push({
              tipo: 'Material',
              nombre: material.texto || material.text,
              especialidad: material.especialidad || material.specialty,
              etapa: 'OC Aprobada',
              presupuestado: proceso.precioPresupuestado,
              cierre: proceso.precioCierre,
              diferencia: proceso.precioPresupuestado - proceso.precioCierre,
              porcentaje: ((proceso.precioPresupuestado - proceso.precioCierre) / proceso.precioPresupuestado * 100).toFixed(2)
            });
          }
        }
      });
    }
    return items;
  };
  
  const itemsPrecios = calcularDatosPrecios();
  
  // Calcular totales
  const totales = itemsPrecios.reduce((acc, item) => {
    acc.presupuestado += item.presupuestado;
    acc.cierre += item.cierre;
    acc.diferencia += item.diferencia;
    return acc;
  }, { presupuestado: 0, cierre: 0, diferencia: 0 });
  
  const porcentajeTotal = totales.presupuestado > 0 
    ? ((totales.diferencia / totales.presupuestado) * 100).toFixed(2)
    : 0;
  
  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(valor);
  };

  // Función para generar reporte PDF
  const generarReportePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 16;
    const contentWidth = pageWidth - margin * 2;
    let yPos = 0;

    // Colores corporativos
    const azul = [30, 58, 95];       // #111827
    const naranja = [249, 115, 22];   // #f97316
    const grisOscuro = [55, 65, 81];  // #374151
    const grisClaro = [156, 163, 175]; // #9ca3af
    const blanco = [255, 255, 255];
    const fondoClaro = [249, 250, 251]; // #f9fafb

    // ==== HEADER: Barra superior azul oscuro ====
    doc.setFillColor(...azul);
    doc.rect(0, 0, pageWidth, 36, 'F');

    // Acento naranja inferior del header
    doc.setFillColor(...naranja);
    doc.rect(0, 36, pageWidth, 2, 'F');

    // Logo "GestraCOO" en blanco sobre azul
    doc.setTextColor(...blanco);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Gestra', margin, 18);
    const logoW = doc.getTextWidth('Gestra');
    doc.setTextColor(...naranja);
    doc.text('COO', margin + logoW, 18);

    // Título del reporte alineado a la derecha
    doc.setTextColor(...blanco);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('REPORTE DE PROYECTO', pageWidth - margin, 14, { align: 'right' });

    // Fecha de generación
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 200, 220);
    doc.text(
      new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
      pageWidth - margin, 22, { align: 'right' }
    );

    // Nombre del proyecto centrado debajo
    if (curvaS.proyecto) {
      doc.setFontSize(9);
      doc.setTextColor(200, 215, 230);
      doc.text(curvaS.proyecto.nombre, pageWidth - margin, 30, { align: 'right' });
    }

    yPos = 46;

    // ==== SECCIÓN: INFORMACIÓN DEL PROYECTO ====
    // Título de sección con línea naranja
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...azul);
    doc.text('INFORMACION DEL PROYECTO', margin, yPos);
    doc.setDrawColor(...naranja);
    doc.setLineWidth(1.5);
    doc.line(margin, yPos + 2, margin + 58, yPos + 2);
    yPos += 10;

    // Info cards en grid (2 columnas)
    const cardHeight = 18;
    const cardGap = 4;
    const halfWidth = (contentWidth - cardGap) / 2;

    const infoCards = [
      { label: 'Progreso Real', value: `${curvaS.proyecto?.porcentajeCompletado || 0}%`, color: naranja },
      { label: 'Procesos', value: `${curvaS.procesosCompletados || 0} / ${curvaS.totalHitos || 0}`, color: azul },
      { label: 'Productividad', value: `${curvaS.proyecto?.velocidadProcesos || 0} proc/sem`, color: azul },
      { label: 'Ritmo de Trabajo', value: `${curvaS.proyecto?.ritmoTrabajo || 0}%`, color: naranja }
    ];

    infoCards.forEach((card, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = margin + col * (halfWidth + cardGap);
      const y = yPos + row * (cardHeight + cardGap);

      // Fondo de tarjeta
      doc.setFillColor(...fondoClaro);
      doc.roundedRect(x, y, halfWidth, cardHeight, 2, 2, 'F');

      // Borde izquierdo de color
      doc.setFillColor(...card.color);
      doc.rect(x, y + 2, 1.5, cardHeight - 4, 'F');

      // Label
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grisClaro);
      doc.text(card.label.toUpperCase(), x + 6, y + 7);

      // Valor
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...azul);
      doc.text(card.value, x + 6, y + 14);
    });

    yPos += (cardHeight + cardGap) * 2 + 6;

    // ==== SECCIÓN: FECHAS ====
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...azul);
    doc.text('FECHAS DE TERMINO', margin, yPos);
    doc.setDrawColor(...naranja);
    doc.setLineWidth(1.5);
    doc.line(margin, yPos + 2, margin + 42, yPos + 2);
    yPos += 8;

    // Tarjeta fecha planificada
    if (curvaS.proyecto?.fechaFin) {
      doc.setFillColor(...fondoClaro);
      doc.roundedRect(margin, yPos, halfWidth, 16, 2, 2, 'F');
      doc.setFillColor(...azul);
      doc.rect(margin, yPos + 2, 1.5, 12, 'F');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grisClaro);
      doc.text('TERMINO PLANIFICADO', margin + 6, yPos + 6);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...azul);
      doc.text(new Date(curvaS.proyecto.fechaFin).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }), margin + 6, yPos + 13);
    }

    // Tarjeta fecha estimada
    if (curvaS.proyecto?.fechaEstimadaTermino) {
      const diasRetraso = curvaS.proyecto.diasRetraso || 0;
      const colorEstado = diasRetraso > 0 ? [239, 68, 68] : diasRetraso < 0 ? [16, 185, 129] : naranja;

      doc.setFillColor(...fondoClaro);
      doc.roundedRect(margin + halfWidth + cardGap, yPos, halfWidth, 16, 2, 2, 'F');
      doc.setFillColor(...colorEstado);
      doc.rect(margin + halfWidth + cardGap, yPos + 2, 1.5, 12, 'F');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grisClaro);
      doc.text('TERMINO ESTIMADO', margin + halfWidth + cardGap + 6, yPos + 6);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colorEstado);
      let textoEstimado = new Date(curvaS.proyecto.fechaEstimadaTermino).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
      if (diasRetraso > 0) textoEstimado += ` (+${diasRetraso}d)`;
      else if (diasRetraso < 0) textoEstimado += ` (${diasRetraso}d)`;
      doc.text(textoEstimado, margin + halfWidth + cardGap + 6, yPos + 13);
    }

    yPos += 24;

    // ==== ANÁLISIS FINANCIERO ====
    if (itemsPrecios.length > 0) {
      // Verificar si necesitamos nueva página
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...azul);
      doc.text('RESUMEN FINANCIERO', margin, yPos);
      doc.setDrawColor(...naranja);
      doc.setLineWidth(1.5);
      doc.line(margin, yPos + 2, margin + 45, yPos + 2);
      yPos += 8;

      const esAhorro = totales.diferencia >= 0;

      // KPI cards financieros (3 columnas)
      const thirdWidth = (contentWidth - cardGap * 2) / 3;
      const kpiCards = [
        { label: 'PRESUPUESTADO', value: formatearMoneda(totales.presupuestado), color: azul },
        { label: 'CIERRE', value: formatearMoneda(totales.cierre), color: azul },
        { label: esAhorro ? 'AHORRO' : 'SOBRECOSTO', value: formatearMoneda(Math.abs(totales.diferencia)), color: esAhorro ? [16, 185, 129] : [239, 68, 68] }
      ];

      kpiCards.forEach((card, i) => {
        const x = margin + i * (thirdWidth + cardGap);
        doc.setFillColor(...fondoClaro);
        doc.roundedRect(x, yPos, thirdWidth, 20, 2, 2, 'F');
        doc.setFillColor(...card.color);
        doc.rect(x, yPos + 3, 1.5, 14, 'F');

        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grisClaro);
        doc.text(card.label, x + 5, yPos + 7);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...card.color);
        doc.text(card.value, x + 5, yPos + 15);
      });

      yPos += 28;

      // Tabla detalle de precios
      if (yPos < pageHeight - 60) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...azul);
        doc.text('DETALLE DE PRECIOS', margin, yPos);
        doc.setDrawColor(...naranja);
        doc.setLineWidth(1.5);
        doc.line(margin, yPos + 2, margin + 42, yPos + 2);
        yPos += 5;

        const datosTabla = itemsPrecios.slice(0, 12).map(item => [
          item.tipo === 'Subcontrato' ? 'Sub' : 'Mat',
          item.nombre.length > 30 ? item.nombre.substring(0, 27) + '...' : item.nombre,
          item.especialidad || '-',
          formatearMoneda(item.presupuestado),
          formatearMoneda(item.cierre),
          `${item.diferencia >= 0 ? '+' : ''}${item.porcentaje}%`
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Tipo', 'Nombre', 'Especialidad', 'Presupuestado', 'Cierre', 'Var.']],
          body: datosTabla,
          theme: 'plain',
          headStyles: {
            fillColor: azul,
            textColor: blanco,
            fontStyle: 'bold',
            fontSize: 7.5,
            cellPadding: 3
          },
          bodyStyles: {
            fontSize: 7,
            cellPadding: 2.5,
            textColor: grisOscuro
          },
          alternateRowStyles: {
            fillColor: fondoClaro
          },
          columnStyles: {
            0: { cellWidth: 13, halign: 'center', fontStyle: 'bold' },
            1: { cellWidth: 45 },
            2: { cellWidth: 28 },
            3: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
            4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
            5: { cellWidth: 18, halign: 'center' }
          },
          didParseCell: (data) => {
            // Color para tipo
            if (data.column.index === 0 && data.section === 'body') {
              data.cell.styles.textColor = data.cell.raw === 'Sub' ? naranja : azul;
            }
            // Color para variación
            if (data.column.index === 5 && data.section === 'body') {
              const item = itemsPrecios[data.row.index];
              if (item) {
                data.cell.styles.textColor = item.diferencia >= 0 ? [16, 185, 129] : [239, 68, 68];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          margin: { left: margin, right: margin }
        });

        if (itemsPrecios.length > 12) {
          yPos = doc.lastAutoTable.finalY + 3;
          doc.setFontSize(7);
          doc.setFont('helvetica', 'italic');
          doc.setTextColor(...grisClaro);
          doc.text(`+ ${itemsPrecios.length - 12} items adicionales`, margin, yPos);
        }
      }
    }

    // ==== PIE DE PÁGINA ====
    // Línea separadora
    doc.setDrawColor(...azul);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);

    // Footer
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...azul);
    doc.text('Gestra', margin, pageHeight - 8);
    doc.setTextColor(...naranja);
    const footerW = doc.getTextWidth('Gestra');
    doc.text('COO', margin + footerW, pageHeight - 8);

    doc.setTextColor(...grisClaro);
    doc.setFontSize(6.5);
    doc.text('Sistema de Gestion de Proyectos de Construccion', margin + footerW + doc.getTextWidth('COO') + 3, pageHeight - 8);
    doc.text(
      `Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES')}`,
      pageWidth - margin, pageHeight - 8, { align: 'right' }
    );

    // Guardar PDF
    const nombreArchivo = `Reporte_${curvaS.proyecto?.nombre || 'Proyecto'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(nombreArchivo);
  };

  // ==================== GENERAR PDF COMO BASE64 ====================
  const generarPDFBase64 = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 16;
    const contentWidth = pageWidth - margin * 2;
    let yPos = 0;

    const azul = [30, 58, 95];
    const naranja = [249, 115, 22];
    const grisOscuro = [55, 65, 81];
    const grisClaro = [156, 163, 175];
    const blanco = [255, 255, 255];
    const fondoClaro = [249, 250, 251];

    // Header
    doc.setFillColor(...azul);
    doc.rect(0, 0, pageWidth, 36, 'F');
    doc.setFillColor(...naranja);
    doc.rect(0, 36, pageWidth, 2, 'F');
    doc.setTextColor(...blanco);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('Gestra', margin, 18);
    const logoW = doc.getTextWidth('Gestra');
    doc.setTextColor(...naranja);
    doc.text('COO', margin + logoW, 18);
    doc.setTextColor(...blanco);
    doc.setFontSize(10);
    doc.text('REPORTE DE PROYECTO', pageWidth - margin, 14, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(180, 200, 220);
    doc.text(new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), pageWidth - margin, 22, { align: 'right' });
    if (curvaS.proyecto) {
      doc.setFontSize(9);
      doc.setTextColor(200, 215, 230);
      doc.text(curvaS.proyecto.nombre, pageWidth - margin, 30, { align: 'right' });
    }
    yPos = 46;

    // Info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...azul);
    doc.text('INFORMACION DEL PROYECTO', margin, yPos);
    doc.setDrawColor(...naranja);
    doc.setLineWidth(1.5);
    doc.line(margin, yPos + 2, margin + 58, yPos + 2);
    yPos += 10;

    const cardHeight = 18;
    const cardGap = 4;
    const halfWidth = (contentWidth - cardGap) / 2;
    const infoCards = [
      { label: 'Progreso Real', value: `${curvaS.proyecto?.porcentajeCompletado || 0}%`, color: naranja },
      { label: 'Procesos', value: `${curvaS.procesosCompletados || 0} / ${curvaS.totalHitos || 0}`, color: azul },
      { label: 'Productividad', value: `${curvaS.proyecto?.velocidadProcesos || 0} proc/sem`, color: azul },
      { label: 'Ritmo de Trabajo', value: `${curvaS.proyecto?.ritmoTrabajo || 0}%`, color: naranja }
    ];
    infoCards.forEach((card, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = margin + col * (halfWidth + cardGap);
      const y = yPos + row * (cardHeight + cardGap);
      doc.setFillColor(...fondoClaro);
      doc.roundedRect(x, y, halfWidth, cardHeight, 2, 2, 'F');
      doc.setFillColor(...card.color);
      doc.rect(x, y + 2, 1.5, cardHeight - 4, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grisClaro);
      doc.text(card.label.toUpperCase(), x + 6, y + 7);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...azul);
      doc.text(card.value, x + 6, y + 14);
    });
    yPos += (cardHeight + cardGap) * 2 + 6;

    // Fechas
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...azul);
    doc.text('FECHAS DE TERMINO', margin, yPos);
    doc.setDrawColor(...naranja);
    doc.setLineWidth(1.5);
    doc.line(margin, yPos + 2, margin + 42, yPos + 2);
    yPos += 8;
    if (curvaS.proyecto?.fechaFin) {
      doc.setFillColor(...fondoClaro);
      doc.roundedRect(margin, yPos, halfWidth, 16, 2, 2, 'F');
      doc.setFillColor(...azul);
      doc.rect(margin, yPos + 2, 1.5, 12, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grisClaro);
      doc.text('TERMINO PLANIFICADO', margin + 6, yPos + 6);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...azul);
      doc.text(new Date(curvaS.proyecto.fechaFin).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }), margin + 6, yPos + 13);
    }
    if (curvaS.proyecto?.fechaEstimadaTermino) {
      const diasRetraso = curvaS.proyecto.diasRetraso || 0;
      const colorEstado = diasRetraso > 0 ? [239, 68, 68] : diasRetraso < 0 ? [16, 185, 129] : naranja;
      doc.setFillColor(...fondoClaro);
      doc.roundedRect(margin + halfWidth + cardGap, yPos, halfWidth, 16, 2, 2, 'F');
      doc.setFillColor(...colorEstado);
      doc.rect(margin + halfWidth + cardGap, yPos + 2, 1.5, 12, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grisClaro);
      doc.text('TERMINO ESTIMADO', margin + halfWidth + cardGap + 6, yPos + 6);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...colorEstado);
      let textoEstimado = new Date(curvaS.proyecto.fechaEstimadaTermino).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
      if (diasRetraso > 0) textoEstimado += ` (+${diasRetraso}d)`;
      else if (diasRetraso < 0) textoEstimado += ` (${diasRetraso}d)`;
      doc.text(textoEstimado, margin + halfWidth + cardGap + 6, yPos + 13);
    }
    yPos += 24;

    // Análisis financiero
    if (itemsPrecios.length > 0) {
      if (yPos > pageHeight - 100) { doc.addPage(); yPos = 20; }
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...azul);
      doc.text('RESUMEN FINANCIERO', margin, yPos);
      doc.setDrawColor(...naranja);
      doc.setLineWidth(1.5);
      doc.line(margin, yPos + 2, margin + 45, yPos + 2);
      yPos += 8;
      const esAhorro = totales.diferencia >= 0;
      const thirdWidth = (contentWidth - cardGap * 2) / 3;
      const kpiCards = [
        { label: 'PRESUPUESTADO', value: formatearMoneda(totales.presupuestado), color: azul },
        { label: 'CIERRE', value: formatearMoneda(totales.cierre), color: azul },
        { label: esAhorro ? 'AHORRO' : 'SOBRECOSTO', value: formatearMoneda(Math.abs(totales.diferencia)), color: esAhorro ? [16, 185, 129] : [239, 68, 68] }
      ];
      kpiCards.forEach((card, i) => {
        const x = margin + i * (thirdWidth + cardGap);
        doc.setFillColor(...fondoClaro);
        doc.roundedRect(x, yPos, thirdWidth, 20, 2, 2, 'F');
        doc.setFillColor(...card.color);
        doc.rect(x, yPos + 3, 1.5, 14, 'F');
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grisClaro);
        doc.text(card.label, x + 5, yPos + 7);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...card.color);
        doc.text(card.value, x + 5, yPos + 15);
      });
      yPos += 28;
      if (yPos < pageHeight - 60) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...azul);
        doc.text('DETALLE DE PRECIOS', margin, yPos);
        doc.setDrawColor(...naranja);
        doc.setLineWidth(1.5);
        doc.line(margin, yPos + 2, margin + 42, yPos + 2);
        yPos += 5;
        const datosTabla = itemsPrecios.slice(0, 12).map(item => [
          item.tipo === 'Subcontrato' ? 'Sub' : 'Mat',
          item.nombre.length > 30 ? item.nombre.substring(0, 27) + '...' : item.nombre,
          item.especialidad || '-',
          formatearMoneda(item.presupuestado),
          formatearMoneda(item.cierre),
          `${item.diferencia >= 0 ? '+' : ''}${item.porcentaje}%`
        ]);
        autoTable(doc, {
          startY: yPos,
          head: [['Tipo', 'Nombre', 'Especialidad', 'Presupuestado', 'Cierre', 'Var.']],
          body: datosTabla,
          theme: 'plain',
          headStyles: { fillColor: azul, textColor: blanco, fontStyle: 'bold', fontSize: 7.5, cellPadding: 3 },
          bodyStyles: { fontSize: 7, cellPadding: 2.5, textColor: grisOscuro },
          alternateRowStyles: { fillColor: fondoClaro },
          columnStyles: {
            0: { cellWidth: 13, halign: 'center', fontStyle: 'bold' },
            1: { cellWidth: 45 },
            2: { cellWidth: 28 },
            3: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
            4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
            5: { cellWidth: 18, halign: 'center' }
          },
          didParseCell: (data) => {
            if (data.column.index === 0 && data.section === 'body') {
              data.cell.styles.textColor = data.cell.raw === 'Sub' ? naranja : azul;
            }
            if (data.column.index === 5 && data.section === 'body') {
              const item = itemsPrecios[data.row.index];
              if (item) {
                data.cell.styles.textColor = item.diferencia >= 0 ? [16, 185, 129] : [239, 68, 68];
                data.cell.styles.fontStyle = 'bold';
              }
            }
          },
          margin: { left: margin, right: margin }
        });
      }
    }

    // Footer
    doc.setDrawColor(...azul);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...azul);
    doc.text('Gestra', margin, pageHeight - 8);
    doc.setTextColor(...naranja);
    const fW = doc.getTextWidth('Gestra');
    doc.text('COO', margin + fW, pageHeight - 8);
    doc.setTextColor(...grisClaro);
    doc.setFontSize(6.5);
    doc.text('Sistema de Gestion de Proyectos de Construccion', margin + fW + doc.getTextWidth('COO') + 3, pageHeight - 8);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES')}`, pageWidth - margin, pageHeight - 8, { align: 'right' });

    // Retornar como base64 puro (sin prefijo data URI)
    const arrayBuffer = doc.output('arraybuffer');
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // ==================== ENVIAR REPORTE POR CORREO ====================
  const enviarReportePorCorreo = async () => {
    if (!emailReporte.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailReporte.trim())) {
      setMensajeReporte({ texto: 'Ingresa un correo electrónico válido', tipo: 'error' });
      return;
    }

    try {
      setEnviandoReporte(true);
      setMensajeReporte({ texto: 'Generando PDF y enviando...', tipo: '' });

      // Generar PDF como base64
      const pdfBase64 = generarPDFBase64();

      // Preparar datos del proyecto desde curvaS
      const datosProyecto = {
        nombre: curvaS.proyecto?.nombre || 'Proyecto',
        fechaInicio: curvaS.proyecto?.fechaInicio || null,
        fechaFin: curvaS.proyecto?.fechaFin || null,
        fechaEstimadaTermino: curvaS.proyecto?.fechaEstimadaTermino || null,
        diasRetraso: curvaS.proyecto?.diasRetraso || 0,
        porcentajeCompletado: curvaS.proyecto?.porcentajeCompletado || '0',
        velocidadProcesos: curvaS.proyecto?.velocidadProcesos || '0',
        ritmoTrabajo: curvaS.proyecto?.ritmoTrabajo || '0',
        totalHitos: curvaS.totalHitos || 0,
        procesosCompletados: curvaS.procesosCompletados || 0
      };

      // Preparar análisis de precios
      let analisisPrecios = null;
      if (itemsPrecios.length > 0) {
        analisisPrecios = {
          items: itemsPrecios.map(item => ({
            tipo: item.tipo,
            nombre: item.nombre,
            especialidad: item.especialidad || '',
            presupuestado: item.presupuestado,
            cierre: item.cierre,
            diferencia: item.diferencia,
            porcentaje: item.porcentaje
          })),
          totales: {
            presupuestado: totales.presupuestado,
            cierre: totales.cierre,
            diferencia: totales.diferencia,
            porcentaje: porcentajeTotal
          }
        };
      }

      const resultado = await dashboardService.enviarReporte(
        emailReporte.trim(),
        datosProyecto,
        analisisPrecios,
        pdfBase64
      );
      setMensajeReporte({ texto: resultado.mensaje || `Reporte enviado a ${emailReporte}`, tipo: 'exito' });
      setEmailReporte('');
      setTimeout(() => {
        setMostrarFormReporte(false);
        setMensajeReporte({ texto: '', tipo: '' });
      }, 3000);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error al enviar el reporte';
      setMensajeReporte({ texto: msg, tipo: 'error' });
    } finally {
      setEnviandoReporte(false);
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
      <div className="dashboard-header">
        <div className="dashboard-title-section">
          <h2>Dashboard del Proyecto</h2>
          {curvaS.proyecto && (
            <p className="dashboard-subtitle">
              {curvaS.proyecto.nombre}
            </p>
          )}
        </div>
        <div className="dashboard-header-buttons">
          <button 
            className="btn-descargar-pdf" 
            onClick={generarReportePDF}
            title="Descargar reporte PDF para gerencia"
          >
            Descargar Reporte PDF
          </button>
          <button 
            className="btn-enviar-reporte" 
            onClick={() => { setMostrarFormReporte(!mostrarFormReporte); setMensajeReporte({ texto: '', tipo: '' }); }}
            title="Enviar reporte del proyecto por correo"
          >
            ✉ Enviar Reporte
          </button>
        </div>
      </div>

      {/* FORMULARIO ENVIAR REPORTE */}
      {mostrarFormReporte && (
        <div className="reporte-form-panel">
          <div className="reporte-form-header">
            <span className="reporte-form-titulo">✉ Enviar Reporte por Correo</span>
            <button className="reporte-form-cerrar" onClick={() => setMostrarFormReporte(false)}>×</button>
          </div>
          <p className="reporte-form-desc">El correo incluirá las métricas del proyecto, progreso actual, y tus datos como remitente.</p>
          <div className="reporte-form-row">
            <input
              type="email"
              className="reporte-form-input"
              placeholder="correo@ejemplo.com"
              value={emailReporte}
              onChange={(e) => setEmailReporte(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && enviarReportePorCorreo()}
              disabled={enviandoReporte}
            />
            <button
              className={`reporte-form-btn ${enviandoReporte ? 'enviando' : ''}`}
              onClick={enviarReportePorCorreo}
              disabled={enviandoReporte || !emailReporte.trim()}
            >
              {enviandoReporte ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
          {mensajeReporte.texto && (
            <div className={`reporte-form-msg ${mensajeReporte.tipo}`}>
              {mensajeReporte.tipo === 'exito' ? '✅' : '⚠️'} {mensajeReporte.texto}
            </div>
          )}
        </div>
      )}

      {/* FILTROS */}
      <div className="dashboard-filters">
        <label>
          <span>Mostrar:</span>
          <select 
            value={filtroTipo} 
            onChange={(e) => setFiltroTipo(e.target.value)}
          >
            <option value="todos">Todos (Subcontratos + Materiales)</option>
            <option value="subcontratos">Solo Subcontratos</option>
            <option value="materiales">Solo Materiales</option>
          </select>
        </label>
      </div>

      {/* FECHAS DE TÉRMINO */}
      {curvaS.proyecto && (
        <div className="dashboard-dates-section">
          <div className="date-card planificada">
            <div className="date-icon">P</div>
            <div className="date-content">
              <span className="date-label">Fecha Planificada de Término</span>
              <span className="date-value">
                {curvaS.proyecto.fechaFin ? (
                  new Date(curvaS.proyecto.fechaFin).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })
                ) : (
                  <span style={{ color: '#94a3b8', fontSize: '16px' }}>
                    No configurada - Configura en la pestaña Proyecto
                  </span>
                )}
              </span>
            </div>
          </div>

          {curvaS.proyecto.fechaEstimadaTermino && (
            <div className={`date-card estimada ${curvaS.proyecto.diasRetraso > 0 ? 'retrasada' : curvaS.proyecto.diasRetraso < 0 ? 'adelantada' : 'a-tiempo'}`}>
              <div className="date-icon">E</div>
              <div className="date-content">
                <span className="date-label">Fecha Estimada de Término Real</span>
                <span className="date-value">
                  {new Date(curvaS.proyecto.fechaEstimadaTermino).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
                <span className="date-detail">
                  {curvaS.proyecto.diasRetraso > 0 && (
                    `${curvaS.proyecto.diasRetraso} días de retraso`
                  )}
                  {curvaS.proyecto.diasRetraso < 0 && (
                    `${Math.abs(curvaS.proyecto.diasRetraso)} días de adelanto`
                  )}
                  {curvaS.proyecto.diasRetraso === 0 && (
                    'A tiempo según lo planificado'
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ALERTAS DE RUTA CRÍTICA */}
      {(subcontratosEnRiesgo.length > 0 || materialesEnRiesgo.length > 0) && (
        <div className="dashboard-section">
          <h3>Alertas de Ruta Crítica</h3>
          <p className="chart-description">
            Subcontratos y materiales vencidos o a punto de vencer (3 días o menos)
          </p>

          <div className="ruta-critica-columnas">
            {subcontratosEnRiesgo.length > 0 && (
              <div className="ruta-critica-resumen">
                <h4>Subcontratos ({subcontratosEnRiesgo.length})</h4>
                <ul className="ruta-critica-lista">
                  {subcontratosEnRiesgo.map((t) => (
                    <li key={t.id}>
                      <span className={`ruta-critica-badge ${t.daysUntilDue < 0 ? 'vencido' : 'critico'}`}>
                        {t.daysUntilDue < 0 ? 'VENCIDO' : 'CRÍTICO'}
                      </span>
                      <span className="ruta-critica-nombre">{t.text}</span>
                      <span className="ruta-critica-dias">{t.daysUntilDue}d</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {materialesEnRiesgo.length > 0 && (
              <div className="ruta-critica-resumen">
                <h4>Materiales ({materialesEnRiesgo.length})</h4>
                <ul className="ruta-critica-lista">
                  {materialesEnRiesgo.map((m) => (
                    <li key={m.id}>
                      <span className={`ruta-critica-badge ${m.daysUntilDue < 0 ? 'vencido' : 'critico'}`}>
                        {m.daysUntilDue < 0 ? 'VENCIDO' : 'CRÍTICO'}
                      </span>
                      <span className="ruta-critica-nombre">{m.text}</span>
                      <span className="ruta-critica-dias">{m.daysUntilDue}d</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Dashboard;